import { Example } from "../models/ExampleModel";
import { ExplanationModel } from "../models/ExplanationModel";
import { ImperativeIrregularModel, ImperativeModel } from "../models/ImperativeModel";

// 🧠 General explanation of the Imperative mood
export const imperativeExplanationData: ExplanationModel = {
    title: "📖 What is Imperativo?",
    description: "The imperative mood is used for various purposes in communication. Here’s a breakdown of its key uses:",
    bulletPoints: [
        "💬 Orders: The imperative is used to give commands or orders.",
        "🔄 Requests: It’s also used to make requests or ask for something.",
        "📋 Instructions: Used to provide clear instructions or directions.",
        "👍 Affirmative(Positive) Commands: These are commands where you ask someone to do something.",
        "❌ Negative Commands: Used to tell someone not to do something, often using the subjunctive mood.",
    ],
    examples: [
        "🏞️ In a Public Park",

        "Sign: 'Mantén el parque limpio.' → Keep the park clean.",
        "Sign: 'Tira la basura en los contenedores.' → Throw the trash in the bins.",
        "Sign: 'Camina por los senderos designados.' → Walk on the designated paths.",
        "Sign: 'No pises el césped.' → Do not step on the grass.",
        "Sign: 'No alimentes a los animales.' → Do not feed the animals."
    ]
};

export const imperativeDialog = `
    Teacher: 'Abre tu libro en la página veinte.'
    Student: '¿Ahora, profesora?'
    Teacher: 'Sí, ahora. Lee el primer párrafo en voz alta, por favor.'
    Student: '¿Después qué hago?' 
    Teacher: 'Escribe una respuesta a la pregunta en tu cuaderno.'
    Teacher: 'Y no uses el traductor esta vez. Confía en tu vocabulario.'
    ㅤㅤㅤㅤㅤㅤㅤ
    
    📌 Notice how the teacher gives both positive and negative commands to the student:
    ✦  'Open your book', 
    ✦  'Read the first paragraph', 
    ✦  'Write an answer in your notebook', etc.
ㅤㅤㅤㅤㅤㅤㅤ
    - 'Abre', 'Lee', 'Escribe', 'Confía' (affirmative)
    - 'No uses' (negative)
`;

// 🧾 Example sentences using imperative
export const imperativeExampleList: Example[] = [
    new Example("¡Estudia todos los días!", "📚 Direct Command – 'Study every day!' This is a command asking someone to adopt a habitual action."),
    new Example("¡Trabaja en una oficina!", "🏢 Direct Command – 'Work in an office!' A simple instruction to someone."),
    new Example("¡Ve al cine cada fin de semana!", "🍿 Repeated Action Command – 'Go to the cinema every weekend!' Encouraging a habit or routine."),
    new Example("¡Ten una casa en la playa!", "🏖️ Possession Command – 'Have a house at the beach!' A directive suggesting a dream or desire."),
    new Example("¡Llega a clase a tiempo!", "⏰ Behavioral Command – 'Arrive to class on time!' Encourages punctuality."),
    new Example("¡Vive en Madrid desde hace tres años!", "🏠 Ongoing Action Command – 'Live in Madrid for three years!' Suggests a long-term decision."),
];

// 📘 Regular verbs with their imperative forms
export const regularImperativeConjugations: ImperativeModel[] = [
    new ImperativeModel("Hablar", ["Habla", "Hable", "Hablemos", "Hablad", "Hablen"], ["No hables", "No hable", "No hablemos", "No habléis", "No hablen"]),
    new ImperativeModel("Comer", ["Come", "Coma", "Comamos", "Comed", "Coman"], ["No comas", "No coma", "No comamos", "No comáis", "No coman"]),
    new ImperativeModel("Escribir", ["Escribe", "Escriba", "Escribamos", "Escribid", "Escriban"], ["No escribas", "No escriba", "No escribamos", "No escribáis", "No escriban"]),
];

export const irregularImperativeConjugations: ImperativeModel[] = [
    new ImperativeModel(
        "Ser",
        ["Sé", "Sea", "Seamos", "Sed", "Sean"],
        ["No seas", "No sea", "No seamos", "No seáis", "No sean"]
    ),
    new ImperativeModel(
        "Ir",
        ["Ve", "Vaya", "Vayamos", "Id", "Vayan"],
        ["No vayas", "No vaya", "No vayamos", "No vayáis", "No vayan"]
    ),
    new ImperativeModel(
        "Tener",
        ["Ten", "Tenga", "Tengamos", "Tened", "Tengan"],
        ["No tengas", "No tenga", "No tengamos", "No tengáis", "No tengan"]
    ),
    new ImperativeModel(
        "Hacer",
        ["Haz", "Haga", "Hagamos", "Haced", "Hagan"],
        ["No hagas", "No haga", "No hagamos", "No hagáis", "No hagan"]
    ),
    new ImperativeModel(
        "Poner",
        ["Pon", "Ponga", "Pongamos", "Poned", "Pongan"],
        ["No pongas", "No ponga", "No pongamos", "No pongáis", "No pongan"]
    ),
    new ImperativeModel(
        "Salir",
        ["Sal", "Salga", "Salgamos", "Salid", "Salgan"],
        ["No salgas", "No salga", "No salgamos", "No salgáis", "No salgan"]
    ),
    new ImperativeModel(
        "Venir",
        ["Ven", "Venga", "Vengamos", "Venid", "Vengan"],
        ["No vengas", "No venga", "No vengamos", "No vengáis", "No vengan"]
    ),
    new ImperativeModel(
        "Decir",
        ["Di", "Diga", "Digamos", "Decid", "Digan"],
        ["No digas", "No diga", "No digamos", "No digáis", "No digan"]
    ),
    new ImperativeModel(
        "Haber",
        ["He", "Haya", "Hayamos", "Habed", "Hayan"],
        ["No hayas", "No haya", "No hayamos", "No hayáis", "No hayan"]
    ),
]

// ✨ Common irregular imperatives with a single form + example
export const commonIrregularImperatives: Example[] = [
    new Example("Digan la verdad en todo momento.", "🗣️ Say the truth at all times. (you all)"),
    new Example("Hagamos una pausa para el café.", "☕ Let's take a break for coffee. (we)"),
    new Example("Vaya a la recepción, por favor.", "📍 Go to the reception, please. (formal you)"),
    new Example("Pon tu nombre en la comida de la nevera.", "🧊 Put your name on the food in the fridge. (you)"),
    new Example("Salgan cuando terminen.", "🚪 Leave when you finish. (you all)"),
    new Example("Seamos claros en nuestra comunicación.", "💬 Let's be clear in our communication. (we)"),
    new Example("Ten en cuenta los ingredientes de temporada.", "🥦 Keep seasonal ingredients in mind. (you)"),
    new Example("Venga a la oficina a las diez.", "🏢 Come to the office at ten. (formal you)")
];
